// ─────────────────────────────────────────────────────────────────────────────
// Physical restraint audit worksheet — who sees the button.
//
// The worksheet is in preview and gated to the system administrator until it is
// deliberately unlocked. This is the CLIENT half of that gate and it is cosmetic:
// it decides whether a button renders. The gate that matters is
// survai-engine/src/api/middleware/admin-preview.mjs, which answers 403 to
// everybody else and fails closed on an empty allowlist.
//
// Both halves exist on purpose and neither is redundant. Without the server gate
// the data is reachable by anyone who opens devtools. Without this one, an
// ordinary user sees a button that always errors, which reads as a broken product
// rather than a locked feature.
//
// UNLOCKING. The server unlocks with POST_INCIDENT_AUDIT_ACCESS=all. This side then
// needs REACT_APP_POST_INCIDENT_AUDIT_OPEN=true at build time, because the browser
// cannot read the server's environment. They are two switches for one decision,
// and the server's is the one that enforces it — flipping only this one shows the
// button to people the API will still refuse.
// ─────────────────────────────────────────────────────────────────────────────

import { canViewEHR } from "../ehr/ehrAccess";

/** True when the build was made with the worksheet unlocked for everyone. */
export function postIncidentAuditIsOpen() {
  return String(process.env.REACT_APP_POST_INCIDENT_AUDIT_OPEN || "").toLowerCase() === "true";
}

/**
 * May this session see the restraint worksheet?
 *
 * Reuses canViewEHR rather than restating the admin rule, so there is one
 * definition of "system administrator" in the client. A second copy is how the
 * two drift and one of them ends up permissive.
 */
export function canViewPostIncidentAudit(session) {
  if (!session) return false;
  if (postIncidentAuditIsOpen()) return true;
  return canViewEHR(session);
}

/**
 * WHICH FACILITY IDENTIFIER THE AUDIT IS KEYED ON — in one place, because two
 * screens disagreed and the disagreement was invisible.
 *
 * The nightly scan writes post_incident_audit.ccn from the PointClickCare facility
 * id it was scheduled with (MDS_SCAN_FACIDS), and the review tables carry the same
 * key. So that is the identifier both screens must use.
 *
 * They did not. The audit home resolved `pcc_fac_id || ccn || id`; Priority Scan
 * launched the same worksheet with App.js's `survaiFacilityId()`, which prefers
 * `facility_id || id`. On a facility object carrying both — which is the normal
 * case — the worksheet opened against a key the scan never wrote, so it came up
 * with nothing pre-filled, and any review started from it was stored under an
 * identifier the audit home does not list. The work was not lost, it was
 * unreachable, and nothing on either screen could have told anyone.
 *
 * Neither screen resolves this for itself any more. If the key ever needs to
 * change, it changes here and both move together.
 */
export function postIncidentAuditCcn(activeFacility, ccnProp = null) {
  if (ccnProp) return ccnProp;
  const f = activeFacility || {};
  return f.pcc_fac_id || f.ccn || f.facility_id || f.id || null;
}

/**
 * THE FAMILY OF WORKSHEETS, IN ONE PLACE — for the reason above, one paragraph later.
 *
 * The audit home declared this list itself, which was fine while it was the only
 * screen that could choose an aspect. Priority Scan can now choose one too, and a
 * second copy of the list is how the two end up offering different sets: an aspect
 * added here and not there is simply missing from one launch point, and nothing
 * fails.
 *
 * `ready` is whether the EVIDENCE RULES are written, not whether the shape exists —
 * the worksheet renders whatever it is handed. The unbuilt ones are listed rather
 * than hidden so a reader can see the tool was designed for a family; a picker
 * showing one entry reads as "this is the audit tool" rather than "this is one of
 * five".
 *
 * The keys are the server's: survai-engine's TRIGGER-side registry keys the compiled
 * worksheet and its rows on them, so a typo here is an empty worksheet rather than a
 * crash. Keep them in step with the aspect compilers in
 * survai-engine/src/clinical-scan/.
 */
export const POST_INCIDENT_ASPECTS = Object.freeze([
  { key: "RESTRAINTS", label: "Nursing — Physical Restraints", ready: true },
  { key: "FALLS", label: "Nursing — Falls", ready: true },
  { key: "PAIN", label: "Nursing — Pain Management", ready: true },
  { key: "PRESSURE_ULCER", label: "Nursing — Pressure Ulcers", ready: true },
  { key: "ELOPEMENT", label: "Nursing — Elopement / Wandering", ready: true },
  // THE SIXTH, AND NOT AN INCIDENT ASPECT. The five above audit a chart after an event; this
  // one is a standing check on the order list, so there is nothing to have happened first.
  // Asked for by name in the 4 October clinical review — "specific medications, high-risk
  // medications, that is an area that would be very helpful. Again, I'm doing that manually."
  { key: "HIGH_RISK_MEDS", label: "Nursing — High-Risk Medications", ready: true },
  // THE SEVENTH, from her supplementary notes: podiatry, dental and ophthalmology against a
  // coded diabetes diagnosis — three consults she checks by hand and calls slow. Like
  // HIGH_RISK_MEDS, the cohort is a diagnosis rather than an event.
  { key: "DIABETIC_CARE", label: "Nursing — Diabetic Care", ready: true },
]);

/** The label for an aspect key, or the key itself if it is not one we know. */
export function postIncidentAspectLabel(key) {
  const hit = POST_INCIDENT_ASPECTS.find((a) => a.key === key);
  return hit ? hit.label : String(key || "");
}

export default canViewPostIncidentAudit;
